import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Check, X, Star, Users, Crown, Zap, Calendar, Video, MapPin, Globe, MessageCircle, Dumbbell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllServices } from '../services/serviceService';
import { getServicePriceSuffix } from '../utils/paymentUtils';

/**
 * Services Page Component
 * Display training programs, pricing tiers, and service offerings
 */
export default function Services() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <ProgramTiersSection />
      <ServiceFeaturesSection />
      <ComparisonSection />
      <FAQSection />
      <CTASection />
    </div>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="relative py-20 bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-semibold">Choose Your Path to Success</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            Training Programs
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Personalized coaching programs designed to fit your lifestyle, goals, and budget.
            In-person in Palm Beach or online coaching worldwide.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Icon mapping function - maps icon_name string to Lucide icon component
const getIconComponent = (iconName) => {
  const iconMap = {
    Users,
    Star,
    MapPin,
    Crown,
    Zap,
    Calendar,
    Video,
    Globe,
    MessageCircle,
    Dumbbell,
    Check,
    X,
  };
  return iconMap[iconName] || Users; // Default to Users if icon not found
};

// Program Tiers Section - Pricing cards
function ProgramTiersSection() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAllServices();
      if (result.success) {
        setServices(result.data || []);
      } else {
        setError(result.error || 'Failed to load services');
      }
    } catch (err) {
      setError(err.message || 'Failed to load services');
      console.error('Error loading services:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const colorClasses = {
    blue: {
      gradient: 'from-blue-500 to-blue-600',
      border: 'border-blue-500',
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      hover: 'hover:bg-blue-600',
      shadow: 'shadow-blue-500/50',
    },
    purple: {
      gradient: 'from-purple-500 to-purple-600',
      border: 'border-purple-500',
      text: 'text-purple-600',
      bg: 'bg-purple-50',
      hover: 'hover:bg-purple-600',
      shadow: 'shadow-purple-500/50',
    },
    primary: {
      gradient: 'from-primary-500 to-primary-600',
      border: 'border-primary-500',
      text: 'text-primary-600',
      bg: 'bg-primary-50',
      hover: 'hover:bg-primary-600',
      shadow: 'shadow-primary-500/50',
    },
    accent: {
      gradient: 'from-accent-500 to-accent-600',
      border: 'border-accent-500',
      text: 'text-accent-600',
      bg: 'bg-accent-50',
      hover: 'hover:bg-accent-600',
      shadow: 'shadow-accent-500/50',
    },
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Choose Your Program
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Select the coaching experience that best fits your goals and commitment level
          </p>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading services...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <p className="text-red-600">Error loading services: {error}</p>
          </div>
        )}

        {/* Empty State - No Active Services */}
        {!isLoading && !error && services.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-2xl mx-auto mb-8">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Service Listings Are Updating
              </h3>
              <p className="text-lg text-slate-600 mb-6">
                We're currently updating our service offerings. In the meantime, let's discuss how we can help you achieve your fitness goals.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="w-5 h-5" />
              Schedule Free Consultation
            </Link>
            <p className="text-sm text-slate-500 mt-4">
              Book a free 30-minute discovery call and we'll help you choose the perfect program
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        {!isLoading && !error && services.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {services.map((service, index) => {
              const Icon = getIconComponent(service.icon_name);
              const colors = colorClasses[service.color];
              const priceSuffix = getServicePriceSuffix(service.renewal_period, service.renewal_interval_months);
              const features = Array.isArray(service.features) ? service.features : [];

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative bg-white rounded-2xl shadow-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                    service.is_popular ? 'ring-4 ring-purple-500 ring-opacity-50' : ''
                  }`}
                >
                  {service.is_popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full font-bold text-xs shadow-lg flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        POPULAR
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-2 rounded-xl bg-gradient-to-br ${colors.gradient} shadow-lg ${colors.shadow}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      {service.max_students && (
                        <div className={`${colors.bg} px-2 py-1 rounded-full`}>
                          <p className={`text-xs font-semibold ${colors.text}`}>
                            {service.max_students}
                          </p>
                        </div>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {service.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">{service.tagline}</p>

                    <div className="mb-4">
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-4xl font-bold text-slate-900">
                          ${parseFloat(service.price).toLocaleString()}
                        </span>
                        <span className="text-slate-600 text-sm">{priceSuffix}</span>
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      className={`w-full block text-center bg-gradient-to-r ${colors.gradient} text-white py-3 rounded-xl font-bold text-sm transition-all duration-300 ${colors.hover} hover:shadow-xl transform hover:scale-105 mb-6`}
                    >
                      Get Started
                    </Link>

                    <div className="space-y-3">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          {feature.included ? (
                            <div className={`p-0.5 rounded-full ${colors.bg} flex-shrink-0 mt-0.5`}>
                              <Check className={`w-3 h-3 ${colors.text}`} />
                            </div>
                          ) : (
                            <div className="p-0.5 rounded-full bg-slate-100 flex-shrink-0 mt-0.5">
                              <X className="w-3 h-3 text-slate-400" />
                            </div>
                          )}
                          <span className={`text-xs ${feature.included ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                            {feature.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA - Only show when services are available */}
        {!isLoading && !error && services.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-center shadow-2xl"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Not sure which program is right for you?
            </h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Book a free 30-minute discovery call and we'll help you choose the perfect path
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-slate-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              <Calendar className="w-5 h-5" />
              Schedule Free Call
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// Service Features Section
function ServiceFeaturesSection() {
  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: 'Virtual Training Sessions',
      description: 'Live video coaching sessions from anywhere in the world with real-time form feedback.'
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'In person & remote training',
      description: 'Face-to-face coaching at our Palm Beach location for hands-on guidance and support.'
    },
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: 'Custom Programming',
      description: 'Personalized workout plans tailored to your goals, fitness level, and available equipment.'
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: 'Daily Check-ins',
      description: 'Regular communication and accountability to keep you motivated and on track.'
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Flexible Scheduling',
      description: 'Training times that work with your busy lifestyle, whether online or in-person.'
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: 'Results Guarantee',
      description: 'Follow the program consistently and you will see measurable improvements.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What's Included
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every program includes comprehensive support to ensure your success
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 mb-6 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 text-white">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Comparison Section
function ComparisonSection() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Online vs. In-Person Training
          </h2>
          <p className="text-xl text-gray-600">
            Both options deliver exceptional results—choose what fits your lifestyle
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-10 h-10 text-purple-600" />
              <h3 className="text-2xl font-bold text-gray-900">Online Coaching</h3>
            </div>
            <ul className="space-y-4">
              {[
                'Train from anywhere in the world',
                'Flexible scheduling around your day',
                'More affordable pricing options',
                'Perfect for busy professionals',
                'Video form reviews & feedback',
                'Full program & nutrition access'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-2xl shadow-lg"
          >
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-10 h-10 text-primary-600" />
              <h3 className="text-2xl font-bold text-gray-900">In-Person Training</h3>
            </div>
            <ul className="space-y-4">
              {[
                'Face-to-face coaching in Palm Beach',
                'Immediate form corrections',
                'Hands-on guidance & spotting',
                'Premium gym equipment access',
                'Highest level of accountability',
                'Faster results & transformation'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// FAQ Section
function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What equipment do I need for online training?',
      answer: 'Programs can be customized for minimal equipment (bodyweight, resistance bands) up to full gym access. During our consultation, we\'ll discuss what you have available and design accordingly.'
    },
    {
      question: 'How long are the programs?',
      answer: 'Most clients see significant results within 12-16 weeks, but programs are ongoing with month-to-month or annual commitment options. We recommend at least 3 months for sustainable transformation.'
    },
    {
      question: 'Do you provide nutrition guidance?',
      answer: 'Yes! All 1-on-1 programs include personalized nutrition recommendations. We create sustainable meal strategies that fit your lifestyle and support your fitness goals.'
    },
    {
      question: 'Can I switch between online and in-person?',
      answer: 'Absolutely! Some clients do hybrid coaching, combining in-person sessions with online support. We\'ll work out a schedule that maximizes your results.'
    },
    {
      question: 'What if I need to pause my program?',
      answer: 'Life happens! We offer flexible pausing options for travel, injury, or personal circumstances. Just communicate with us and we\'ll work it out.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Got questions? We've got answers.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                <span className="text-2xl text-primary-600 flex-shrink-0">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section className="py-20 bg-gradient-to-r from-primary-700 to-primary-600">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Choose your program and take the first step toward your transformation today.
          </p>

          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:scale-105 transform transition-all duration-300"
          >
            <Calendar className="w-5 h-5" />
            Book Free Consultation
          </Link>

          <p className="mt-6 text-white/70 text-sm">
            30-minute discovery call • No pressure • No commitment
          </p>
        </motion.div>
      </div>
    </section>
  );
}
